import { useUserInfo } from "@features/auth/useAuth";
import { useAsyncMutation } from "@hooks/useAsyncMutation";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { Avatar, AvatarFallback } from "@shadcn/ui/avatar";
import { Button } from "@shadcn/ui/button";
import { KeyboardAwareDrawer, KeyboardAwareDrawerContent } from "@components/KeyboardAwareDrawer";
import { Checkbox } from "@shadcn/ui/checkbox";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@shadcn/ui/drawer";
import { Input } from "@shadcn/ui/input";
import { useRouter } from "@tanstack/react-router";
import { Building2, Mail, Search, Trash2, X } from "lucide-react";
import { Suspense, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { getInitials, type LockPermission } from "./ShareAccessDrawer";
import type {
	ShareAccessAccountDrawerReservationQuery,
	ShareAccessAccountDrawerReservationQuery$data,
} from "./__generated__/ShareAccessAccountDrawerReservationQuery.graphql";
import type { ShareAccessAccountDrawerRevokeMutation } from "./__generated__/ShareAccessAccountDrawerRevokeMutation.graphql";
import type { ShareAccessAccountDrawerShareMutation } from "./__generated__/ShareAccessAccountDrawerShareMutation.graphql";
import type { ShareAccessAccountDrawerUpdateMutation } from "./__generated__/ShareAccessAccountDrawerUpdateMutation.graphql";
import type { ShareAccessAccountDrawerOrgMembersQuery } from "./__generated__/ShareAccessAccountDrawerOrgMembersQuery.graphql";

const ALL_PERMISSIONS: LockPermission[] = ["UNLOCK", "END_RENTAL"];

type ReservationGrant = NonNullable<
	NonNullable<ShareAccessAccountDrawerReservationQuery$data["reservation"]>["utilizationGrants"]
>["nodes"][number];

// The list of people a reservation is shared with is only readable by the
// reservation owner. We fetch it lazily here (only when the owner opens this
// drawer) instead of as a fragment on the My Locks list query — otherwise a
// guest grantee's row would request an unauthorized field and break the page.
const reservationGrantsQuery = graphql`
	query ShareAccessAccountDrawerReservationQuery($reservationId: UUID!) {
		reservation(id: $reservationId) {
			... on LockReservation {
				id
				utilizationGrants(filter: { active: true }, pagination: { page: 0, pageSize: 1000 }) @catch(to: NULL) {
					nodes {
						id
						ty
						permissions
						toUser {
							id
							name
							email
						}
						toOrganization {
							id
							name
						}
						toEmail
					}
				}
			}
		}
	}
`;

// Suggestions are limited to members of the current user's organization so we
// never expose other users' emails to people outside their org.
const orgMembersQuery = graphql`
	query ShareAccessAccountDrawerOrgMembersQuery($organizationId: UUID!) {
		organizationMembers(filter: { organizationId: $organizationId }, pagination: { page: 0, pageSize: 1000000 }) {
			nodes {
				id
				user {
					id
					email
					name
				}
			}
		}
	}
`;

const shareMutation = graphql`
	mutation ShareAccessAccountDrawerShareMutation($input: UtilizationGrantCreateInput!) {
		lockUtilizationGrantCreate(input: $input) {
			id
			permissions
		}
	}
`;

const updateMutation = graphql`
	mutation ShareAccessAccountDrawerUpdateMutation($input: UtilizationGrantUpdateInput!) {
		lockUtilizationGrantUpdate(input: $input) {
			id
			permissions
		}
	}
`;

const revokeMutation = graphql`
	mutation ShareAccessAccountDrawerRevokeMutation($id: UUID!) {
		lockUtilizationGrantDeactivate(id: $id) {
			id
			deactivatedAt
		}
	}
`;

type ShareAccessDrawerProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	reservationId: string;
	isB2C?: boolean;
};

export function ShareAccessAccountDrawer(props: ShareAccessDrawerProps) {
	const i18n = useI18nContext().LL["new-ui"];
	return (
		<KeyboardAwareDrawer open={props.open} onOpenChange={props.onOpenChange}>
			<KeyboardAwareDrawerContent>
				<DrawerHeader className="flex-row items-start justify-between gap-4 text-left">
					<div className="flex flex-col gap-1">
						<DrawerTitle className="text-2xl font-breit font-medium">
							<T>{props.isB2C ? i18n.LOCK_SHARE_PERSON_TITLE() : i18n.LOCK_SHARE_ACCOUNT_TITLE()}</T>
						</DrawerTitle>
						<DrawerDescription>
							<T>{props.isB2C ? i18n.LOCK_SHARE_PERSON_DESC() : i18n.LOCK_SHARE_ACCOUNT_DESC()}</T>
						</DrawerDescription>
					</div>
					<DrawerClose asChild>
						<Button variant="ghost" size="icon" className="shrink-0">
							<X className="size-5" />
						</Button>
					</DrawerClose>
				</DrawerHeader>

				{props.open ? (
					<Suspense fallback={<div className="px-4 pb-4 text-sm text-muted-foreground">…</div>}>
						<ShareAccessBody reservationId={props.reservationId} />
					</Suspense>
				) : null}
			</KeyboardAwareDrawerContent>
		</KeyboardAwareDrawer>
	);
}

function ShareAccessBody(props: { reservationId: string }) {
	const i18n = useI18nContext().LL["new-ui"];
	const router = useRouter();
	const me = useUserInfo();
	const reservationData = useLazyLoadQuery<ShareAccessAccountDrawerReservationQuery>(reservationGrantsQuery, {
		reservationId: props.reservationId,
	});
	const reservation = reservationData.reservation;

	const [shareGrant, isSharing] = useAsyncMutation<ShareAccessAccountDrawerShareMutation>(shareMutation);
	const [updateGrant, isUpdating] = useAsyncMutation<ShareAccessAccountDrawerUpdateMutation>(updateMutation);
	const [revokeGrant, isRevoking] = useAsyncMutation<ShareAccessAccountDrawerRevokeMutation>(revokeMutation);
	const isBusy = isSharing || isUpdating || isRevoking;

	const [search, setSearch] = useState("");
	const [permissions, setPermissions] = useState<LockPermission[]>(["UNLOCK"]);
	// Revoking access is irreversible, so we confirm in a drawer before deactivating.
	const [revokeTarget, setRevokeTarget] = useState<ReservationGrant | null>(null);

	// Only platform administrators can look up recipients; everyone else supplies an exact email.
	const organizationId = me?.organization?.id ?? null;
	const canListOrganizationMembers = me?.role === "baceadmin";
	const orgData = useLazyLoadQuery<ShareAccessAccountDrawerOrgMembersQuery>(
		orgMembersQuery,
		{ organizationId: organizationId ?? "" },
		{ fetchPolicy: organizationId && canListOrganizationMembers ? "store-or-network" : "store-only" }
	);
	const orgUsers = organizationId && canListOrganizationMembers ? orgData.organizationMembers.nodes.map((member) => member.user) : [];

	const existingGrants = reservation?.utilizationGrants?.nodes ?? [];
	const grantedUserIds = new Set(existingGrants.filter((g) => g.ty === "USER" && g.toUser).map((g) => g.toUser!.id));

	const q = search.trim().toLowerCase();
	const suggestions =
		q.length >= 2
			? orgUsers.filter(
					(user) =>
						user.id !== me?.id &&
						!grantedUserIds.has(user.id) &&
						(user.email.toLowerCase().includes(q) || (user.name ?? "").toLowerCase().includes(q))
				)
			: [];

	const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(search.trim());

	const togglePermission = (permission: LockPermission) => {
		setPermissions((current) => (current.includes(permission) ? current.filter((item) => item !== permission) : [...current, permission]));
	};

	const handleShare = async (userId?: string) => {
		if (isBusy || permissions.length === 0 || (!userId && !isEmail)) return;
		const email = search.trim().toLowerCase();
		try {
			const existing = existingGrants.find(
				(g) => g.ty === "USER" && (userId ? g.toUser?.id === userId : g.toUser?.email.toLowerCase() === email)
			);
			if (existing) {
				await updateGrant({ variables: { input: { id: existing.id, permissions } } });
			} else {
				await shareGrant({
					variables: {
						input: { reservationId: props.reservationId, permissions, to: userId ? { user: { userId } } : { userEmail: { email } } },
					},
				});
			}
			toast.success(i18n.LOCK_SHARE_SUCCESS());
			setSearch("");
			await router.invalidate();
		} catch (error) {
			console.error(error);
			toast.error(
				error instanceof Error && error.message.includes("Account not found") ? i18n.LOCK_SHARE_NO_EMAIL_MATCH() : i18n.LOCK_SHARE_FAILED()
			);
		}
	};

	const handleUpdatePermissions = async (grantId: string, next: LockPermission[]) => {
		try {
			await updateGrant({ variables: { input: { id: grantId, permissions: next } } });
			toast.success(i18n.LOCK_SHARE_PERMISSIONS_UPDATED());
			await router.invalidate();
		} catch (error) {
			console.error(error);
			toast.error(i18n.LOCK_SHARE_PERMISSIONS_UPDATE_FAILED());
		}
	};

	const handleRevoke = async (grantId: string) => {
		try {
			await revokeGrant({ variables: { id: grantId } });
			toast.success(i18n.LOCK_SHARE_REMOVE_SUCCESS());
			setRevokeTarget(null);
			await router.invalidate();
		} catch (error) {
			console.error(error);
			toast.error(i18n.LOCK_SHARE_REMOVE_FAILED());
		}
	};

	return (
		<div className="flex max-h-[70vh] flex-col gap-6 overflow-y-auto px-4 pb-4">
			<div className="flex flex-col gap-2">
				<span className="text-sm font-medium">
					<T>{i18n.LOCK_SHARE_USER_EMAIL()}</T>
				</span>
				<div className="flex gap-2">
					<div className="relative flex-1">
						<Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							value={search}
							placeholder={canListOrganizationMembers ? i18n.LOCK_SHARE_USER_EMAIL_PLACEHOLDER() : i18n.LOCK_SHARE_EXACT_EMAIL_PLACEHOLDER()}
							onChange={(e) => {
								setSearch(e.target.value);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter" && isEmail) {
									e.preventDefault();
									void handleShare();
								}
							}}
							className="pl-9"
						/>
					</div>
					<Button
						type="button"
						variant="outline"
						className="shrink-0"
						disabled={!isEmail || isBusy || permissions.length === 0}
						onClick={() => void handleShare()}
					>
						<T>{i18n.LOCK_SHARE_SUBMIT()}</T>
					</Button>
				</div>

				{suggestions.length > 0 ? (
					<div className="max-h-[200px] overflow-y-auto rounded-md border">
						{suggestions.map((user) => (
							<button
								key={user.id}
								type="button"
								disabled={isBusy || permissions.length === 0}
								className="flex w-full flex-col gap-0.5 border-b p-2.5 text-left transition-colors last:border-b-0 hover:bg-accent disabled:opacity-50"
								onClick={() => handleShare(user.id)}
							>
								<span className="text-sm font-medium">{user.name || user.email}</span>
								{user.name && <span className="text-xs text-muted-foreground">{user.email}</span>}
							</button>
						))}
					</div>
				) : q.length >= 2 && !isEmail ? (
					<p className="text-sm text-muted-foreground">
						<T>{i18n.LOCK_SHARE_NO_USERS_FOUND()}</T>
					</p>
				) : null}
			</div>

			<div className="flex flex-col gap-2">
				<span className="text-sm font-medium">
					<T>{i18n.LOCK_SHARE_PERMISSIONS_LABEL()}</T>
				</span>
				<div className="flex flex-col gap-2 rounded-md border p-3">
					<PermissionRow
						checked={permissions.includes("UNLOCK")}
						onChange={() => togglePermission("UNLOCK")}
						label={i18n.LOCK_SHARE_PERMISSION_UNLOCK()}
						description={i18n.LOCK_SHARE_PERMISSION_UNLOCK_DESC()}
					/>
					<PermissionRow
						checked={permissions.includes("END_RENTAL")}
						onChange={() => togglePermission("END_RENTAL")}
						label={i18n.LOCK_SHARE_PERMISSION_END_RENTAL()}
						description={i18n.LOCK_SHARE_PERMISSION_END_RENTAL_DESC()}
					/>
				</div>
				{permissions.length === 0 ? (
					<p className="text-sm text-destructive">
						<T>{i18n.LOCK_SHARE_PERMISSION_REQUIRED()}</T>
					</p>
				) : null}
			</div>

			<div className="flex flex-col gap-2">
				<span className="text-sm font-medium">
					<T>{i18n.LOCK_SHARE_PEOPLE_TITLE()}</T>
				</span>
				{existingGrants.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						<T>{i18n.LOCK_SHARE_PEOPLE_EMPTY()}</T>
					</p>
				) : (
					<ul className="flex flex-col divide-y rounded-md border">
						{existingGrants.map((existing) => (
							<SharedPersonRow
								key={existing.id}
								grant={existing}
								isBusy={isBusy}
								onUpdatePermissions={handleUpdatePermissions}
								onRevoke={setRevokeTarget}
							/>
						))}
					</ul>
				)}
			</div>

			<DrawerFooter className="px-0">
				<DrawerClose asChild>
					<Button variant="outline" disabled={isBusy}>
						<T>{i18n.LOCK_CONTENT_CANCEL()}</T>
					</Button>
				</DrawerClose>
			</DrawerFooter>

			<RevokeAccessDrawer
				grant={revokeTarget}
				isBusy={isBusy}
				onCancel={() => setRevokeTarget(null)}
				onConfirm={() => revokeTarget && void handleRevoke(revokeTarget.id)}
			/>
		</div>
	);
}

/** Confirmation drawer shown before an access is revoked (irreversible). */
function RevokeAccessDrawer(props: { grant: ReservationGrant | null; isBusy: boolean; onCancel: () => void; onConfirm: () => void }) {
	const i18n = useI18nContext().LL["new-ui"];
	const { grant, isBusy, onCancel, onConfirm } = props;

	const name = grant?.ty === "ORGANIZATION" ? grant.toOrganization?.name : grant?.ty === "ANONYMOUS" ? null : grant?.toUser?.name;
	const email = grant?.ty === "ORGANIZATION" ? null : grant?.ty === "ANONYMOUS" ? grant.toEmail : grant?.toUser?.email;

	return (
		<Drawer open={grant !== null} onOpenChange={(open) => !open && onCancel()}>
			<DrawerContent>
				<DrawerHeader className="flex-row items-start justify-between gap-4 text-left">
					<div className="flex flex-col gap-1">
						<DrawerTitle className="text-2xl font-breit font-medium">
							<T>{i18n.LOCK_SHARE_REVOKE_CONFIRM_TITLE()}</T>
						</DrawerTitle>
						<DrawerDescription className="text-sm text-muted-foreground">
							<T>{i18n.LOCK_SHARE_REVOKE_CONFIRM_DESC()}</T>
						</DrawerDescription>
					</div>
					<DrawerClose asChild>
						<Button variant="ghost" size="icon" className="shrink-0" disabled={isBusy}>
							<X className="size-5" />
						</Button>
					</DrawerClose>
				</DrawerHeader>

				{grant ? (
					<div className="flex items-center gap-3 border-t px-4 py-4">
						<Avatar className="size-12 shrink-0">
							<AvatarFallback>{getInitials(name, email)}</AvatarFallback>
						</Avatar>
						<div className="flex min-w-0 flex-col">
							<span className="truncate text-base font-medium">{name || email || "-"}</span>
							{name && email ? <span className="truncate text-sm text-muted-foreground">{email}</span> : null}
						</div>
					</div>
				) : null}

				<DrawerFooter className="border-t">
					<Button
						variant="destructive"
						className="w-full shadow-xs"
						disabled={isBusy}
						onClick={(e) => {
							e.preventDefault();
							onConfirm();
						}}
					>
						<T>{i18n.LOCK_SHARE_REVOKE_CONFIRM_CTA()}</T>
					</Button>
					<DrawerClose asChild>
						<Button variant="outline" className="w-full" disabled={isBusy}>
							<T>{i18n.LOCK_SHARE_REVOKE_CANCEL()}</T>
						</Button>
					</DrawerClose>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
}

function SharedPersonRow(props: {
	grant: ReservationGrant;
	isBusy: boolean;
	onUpdatePermissions: (grantId: string, permissions: LockPermission[]) => Promise<void>;
	onRevoke: (grant: ReservationGrant) => void;
}) {
	const i18n = useI18nContext().LL["new-ui"];
	const { grant, isBusy, onUpdatePermissions, onRevoke } = props;
	const grantPermissions = (grant.permissions ?? []) as LockPermission[];

	const isOrganization = grant.ty === "ORGANIZATION";
	const isAnonymous = grant.ty === "ANONYMOUS";
	const name = isOrganization ? grant.toOrganization?.name : isAnonymous ? null : grant.toUser?.name;
	const email = isOrganization ? null : isAnonymous ? grant.toEmail : grant.toUser?.email;
	const primaryLabel = name || email || "-";

	const toggle = (permission: LockPermission) => {
		const next = grantPermissions.includes(permission)
			? grantPermissions.filter((item) => item !== permission)
			: [...grantPermissions, permission];
		if (next.length === 0) return;
		void onUpdatePermissions(grant.id, next);
	};

	return (
		<li className="flex flex-col gap-2 p-3">
			<div className="flex items-center justify-between gap-3">
				<div className="flex min-w-0 items-center gap-2.5">
					<Avatar className="size-10 shrink-0">
						<AvatarFallback>
							{isOrganization ? <Building2 className="size-4" /> : isAnonymous ? <Mail className="size-4" /> : getInitials(name, email)}
						</AvatarFallback>
					</Avatar>
					<div className="flex min-w-0 flex-col">
						<span className="truncate text-sm font-medium">{primaryLabel}</span>
						{name && email ? <span className="truncate text-xs text-muted-foreground">{email}</span> : null}
					</div>
				</div>
				<Button
					variant="destructive"
					size="icon"
					className="shrink-0"
					disabled={isBusy}
					aria-label={i18n.LOCK_SHARE_REMOVE()}
					onClick={() => onRevoke(grant)}
				>
					<Trash2 className="size-4" />
				</Button>
			</div>
			<div className="flex flex-col gap-1.5 pl-[3.25rem]">
				{ALL_PERMISSIONS.map((permission) => (
					<label key={permission} className="flex cursor-pointer items-center gap-2">
						<Checkbox checked={grantPermissions.includes(permission)} disabled={isBusy} onCheckedChange={() => toggle(permission)} />
						<span className="text-xs">
							{permission === "UNLOCK" ? i18n.LOCK_SHARE_PERMISSION_UNLOCK() : i18n.LOCK_SHARE_PERMISSION_END_RENTAL()}
						</span>
					</label>
				))}
			</div>
		</li>
	);
}

function PermissionRow(props: { checked: boolean; onChange: () => void; label: string; description: string }) {
	return (
		<label className="flex cursor-pointer items-start gap-3">
			<Checkbox checked={props.checked} onCheckedChange={props.onChange} className="mt-0.5" />
			<span className="flex flex-col">
				<span className="text-sm font-medium">{props.label}</span>
				<span className="text-xs text-muted-foreground">{props.description}</span>
			</span>
		</label>
	);
}
